import { FtIn } from './ft-in.js';
import { BarSize } from './bar-size.js';
import { createEstimateDefaults } from './types.js';
// --- FtIn fields by interface ---
const FTIN_LINE_FIELDS = ['span', 'spacing', 'L1', 'L2', 'L3', 'L4', 'L5', 'stockLength'];
const FTIN_DEFAULTS_FIELDS = [
    'tieClearance', 'spiralClearance', 'spacingTolerance',
    'stockBarLength', 'minBarLength',
];
const FTIN_BARMARK_FIELDS = ['theoreticalLength', 'bendDeduction', 'pinDiameter'];
// --- Serialize ---
export function serializeJob(job) {
    return JSON.stringify(job, replacer, 2);
}
function replacer(_key, value) {
    if (value instanceof FtIn)
        return { __type: 'FtIn', value: value.toString() };
    if (value instanceof BarSize)
        return { __type: 'BarSize', value: value.toString() };
    return value;
}
// --- Deserialize ---
export function deserializeJob(json) {
    const raw = JSON.parse(json);
    // Restore defaults
    raw.defaults = restoreDefaults(raw.defaults);
    // Restore segments and their lines
    raw.segments = (raw.segments ?? []).map((s) => {
        s.lines = (s.lines ?? []).map(restoreLine);
        return s;
    });
    // Restore bar marks
    raw.barMarks = (raw.barMarks ?? []).map(restoreBarMark);
    // Subset queries + notes: plain JSON, no revivers needed; just default
    // to empty arrays when older fixtures don't have the fields.
    if (raw.subsetQueries === undefined)
        raw.subsetQueries = [];
    if (raw.notes === undefined)
        raw.notes = [];
    return raw;
}
function reviveFtIn(v) {
    if (v === null || v === undefined)
        return null;
    if (typeof v === 'object' && v !== null && '__type' in v && v.__type === 'FtIn') {
        return FtIn.parse(v.value);
    }
    if (typeof v === 'string')
        return FtIn.parse(v);
    return null;
}
function reviveBarSize(v) {
    if (v === null || v === undefined)
        return null;
    if (typeof v === 'object' && v !== null && '__type' in v && v.__type === 'BarSize') {
        return BarSize.parse(v.value);
    }
    if (typeof v === 'string')
        return BarSize.parse(v);
    return null;
}
function restoreDefaults(raw) {
    const defaults = { ...raw };
    for (const field of FTIN_DEFAULTS_FIELDS) {
        defaults[field] = reviveFtIn(defaults[field]) ?? createEstimateDefaults()[field];
    }
    return defaults;
}
function restoreLine(raw) {
    const line = { ...raw };
    for (const field of FTIN_LINE_FIELDS) {
        line[field] = reviveFtIn(line[field]);
    }
    line.size = reviveBarSize(line.size);
    // meshSheet: backward-compat default for older fixtures without the field
    if (line.meshSheet === undefined)
        line.meshSheet = null;
    // tags: backward-compat default for older fixtures without the field
    if (line.tags === undefined)
        line.tags = [];
    return line;
}
function restoreBarMark(raw) {
    const mark = { ...raw };
    for (const field of FTIN_BARMARK_FIELDS) {
        mark[field] = reviveFtIn(mark[field]) ?? FtIn.zero();
    }
    mark.size = reviveBarSize(mark.size) ?? BarSize.fromImperial(3);
    // Restore dimensions map (Record<string, FtIn>)
    if (mark.dimensions && typeof mark.dimensions === 'object') {
        const dims = mark.dimensions;
        const restored = {};
        for (const [k, v] of Object.entries(dims)) {
            const ftIn = reviveFtIn(v);
            if (ftIn)
                restored[k] = ftIn;
        }
        mark.dimensions = restored;
    }
    return mark;
}
